import React, { useRef, useState, useEffect } from 'react';
import { processSteps } from '../data/mock';
import { Check } from 'lucide-react';

const Process = () => {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = rect.height;
      const scrollProgress = Math.max(0, -rect.top + window.innerHeight / 2);
      const stepHeight = sectionHeight / processSteps.length;
      const newActiveStep = Math.min(
        Math.floor(scrollProgress / stepHeight),
        processSteps.length - 1
      );
      setActiveStep(Math.max(0, newActiveStep));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section ref={sectionRef} className="bg-black py-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-white/40 text-sm uppercase tracking-widest mb-4">
            How we work
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            A simple 3-step system<br />to get fundable fast.
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            No technical background required. We guide the build end-to-end.
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid md:grid-cols-2 gap-12 md:gap-20">
          {/* Left: Steps */}
          <div className="space-y-10">
            {processSteps.map((step, index) => (
              <div
                key={step.id}
                className={`relative pl-8 border-l-2 transition-all duration-500 cursor-pointer ${
                  activeStep >= index ? 'border-white' : 'border-white/20'
                }`}
                onClick={() => setActiveStep(index)}
              >
                {/* Step indicator */}
                <div
                  className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full transition-all duration-500 ${
                    activeStep >= index ? 'bg-white' : 'bg-white/20'
                  }`}
                />

                <div
                  className={`transition-all duration-500 ${
                    activeStep === index ? 'opacity-100' : 'opacity-50'
                  }`}
                >
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                      activeStep === index
                        ? 'bg-white text-black'
                        : 'bg-white/10 text-white/60'
                    }`}
                  >
                    {step.tag}
                  </span>
                  <h3 className="text-xl md:text-2xl font-semibold text-white mb-4">
                    {step.title}
                  </h3>
                  <ul className="space-y-2">
                    {step.bullets.map((bullet, bulletIndex) => (
                      <li key={bulletIndex} className="flex items-start gap-3">
                        <Check className="w-4 h-4 text-white/60 mt-1 flex-shrink-0" />
                        <span className="text-white/60 leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Visual */}
          <div className="relative">
            <div className="sticky top-32">
              <div className="aspect-square bg-gradient-to-br from-white/5 to-white/[0.02] rounded-3xl border border-white/10 p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-white/10 flex items-center justify-center">
                    <span className="text-5xl font-bold text-white">
                      {activeStep + 1}
                    </span>
                  </div>
                  <h4 className="text-2xl font-semibold text-white mb-2">
                    {processSteps[activeStep]?.tag}
                  </h4>
                  <p className="text-white/50">
                    Step {activeStep + 1} of {processSteps.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;